import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { RequirementCategory } from '../types';

/**
 * Display label for a category the AI assigned.
 *
 * Every taxonomy value is spelled out as its own `t()` call rather than built
 * from a template, so scripts/check-i18n-keys.py can verify each key exists.
 * A value outside the taxonomy is shown as it came back, so the user can
 * still see and filter by it.
 */
export function useCategoryLabel(): (category: RequirementCategory) => string {
  const { t } = useTranslation('requirement-doctor');
  return useCallback(
    (category: RequirementCategory) => {
      switch (category) {
        case 'functional':
          return t('common.categories.functional');
        case 'non_functional':
          return t('common.categories.non_functional');
        case 'safety':
          return t('common.categories.safety');
        case 'performance':
          return t('common.categories.performance');
        case 'interface':
          return t('common.categories.interface');
        case 'security':
          return t('common.categories.security');
        case 'reliability':
          return t('common.categories.reliability');
        case 'hmi':
          return t('common.categories.hmi');
        case 'diagnostics':
          return t('common.categories.diagnostics');
        case 'operational':
          return t('common.categories.operational');
        case 'Unknown':
          return t('common.categories.unknown');
        default:
          return category;
      }
    },
    [t],
  );
}
